import { UAParser } from 'ua-parser-js';

type InAppBrowser =
  | 'inapp_ios_line'
  | 'inapp_ios_facebook'
  | 'inapp_android_yahoo'
  | 'inapp_android_instagram'
  | 'inapp_android_gunosy'
  | 'inapp_android_line'
  | 'inapp_android_facebook';

const MinimumVersion = {
  iOS: 11,
  android: 6
} as const;

/**
 * サポートしないブラウザ
 *
 * SkyLeap はUAParser.jsで判定できないため、isSupportedBrowser()内で判定する
 * @see https://github.com/faisalman/ua-parser-js#methods (getBrowser method)
 *
 * iOSのPuffinブラウザは　Puffin Incognito Browser / Puffin Web Browser の2つがある
 * Puffin Web BrowserはApplicant（Vue）でブラウザ判定が動作しないのでエラー表示にできない（そもそも動かないはず）
 * ApplicantJS上では正常にブザウザ判定が動作する
 */
const unsupportedAndroidBrowsers = ['Firefox', 'Edge', 'Puffin'];
const unsupportedIOSBrowsers = ['Puffin'];

const ua = new UAParser();
const os = ua.getOS();
const osMajorVersion = os.version ? parseInt(os.version.split('.')[0], 10) : 0;
const browser = ua.getBrowser();
const device = ua.getDevice();
const isMobile = device.type ? ['mobile', 'tablet'].includes(device.type) : false; // mobile,tablet = mobile扱い
const isIPad = device.model === 'iPad' || (os.name === 'Mac OS' && navigator.maxTouchPoints > 1); // iPadのsafariはdesktop mode時にMac OS扱いになるので、maxTouchPointsと併せて判定する
const isPC = !isMobile && !isIPad; // mobile,tablet, ipad以外 = PC扱い

/**
 * Os Type, only supports Android/iOS/iPad/PC
 */
export enum OsType {
  PC = 'Pc',
  iOS = 'iOS',
  Android = 'AndroidOS',
  iPad = 'iPad',
  Unsupported = 'Unsupported'
}

/**
 * OS判定
 * @see https://github.com/faisalman/ua-parser-js#methods (getOS method)
 */
export const getOSType = (): OsType => {
  if (isPC) return OsType.PC;
  if (isIPad) return OsType.iPad;
  if (os.name === 'iOS') return OsType.iOS;
  if (os.name === 'Android' || os.name === 'Android-x86') return OsType.Android;

  return OsType.Unsupported;
};

/**
 * 対応OSかどうか
 */
export const isSupportedOSType = (): boolean => {
  const osType = getOSType();
  return [OsType.PC, OsType.iOS, OsType.Android, OsType.iPad].includes(osType);
};

/**
 * 対応OSバージョンかどうか
 */
export const isSupportedOSVersion = (): boolean => {
  const osType = getOSType();
  if (osType === OsType.PC) return true;
  if (osType === OsType.iPad) return true; // iPad OSはiOS 13以上なので常にtrue

  if (osType === OsType.iOS && osMajorVersion >= MinimumVersion.iOS) return true;
  if (osType === OsType.Android && osMajorVersion >= MinimumVersion.android) return true;

  return false;
};

/**
 * 対応ブラウザかどうか
 */
export const isSupportedBrowser = (): boolean => {
  const osType = getOSType();

  if (osType === OsType.PC && browser.name !== 'IE') return true;

  if (osType === OsType.iOS || osType === OsType.iPad) {
    // EKYCDEV-1971 離脱率改善のためにiOSではブラウザ判定をやめる

    // 例外：EKYCDEV-2830 一部サポート外とする
    if (unsupportedIOSBrowsers.includes(browser.name || '')) return false;

    if (checkInAppBrowser() !== null) return false;
    return true;
  }

  if (osType === OsType.Android) {
    // SkyLeap はUAParser.jsで判定できないため、ここで判定する
    const uaString = ua.getUA();
    if (uaString.includes('SkyLeap')) return false;

    if (unsupportedAndroidBrowsers.includes(browser.name || '')) return false;
    if (checkInAppBrowser() !== null) return false;

    return true;
  }

  return false;
};

/**
 * アプリ内ブラウザ判定
 */
export const checkInAppBrowser = (): InAppBrowser | null => {
  const osType = getOSType();
  const uaString = ua.getUA();

  // if (osType === OsType.iOS || osType === OsType.iPad) {
  //   if (browser.name === 'Line') return 'inapp_ios_line';
  //   if (browser.name === 'Facebook') return 'inapp_ios_facebook';
  // }

  if (osType === OsType.Android) {
    // Yahoo!ブラウザ, GunosyはUAParser.jsで判定できないため、UA文字列から判定する
    if (uaString.includes('YJApp-ANDROID')) return 'inapp_android_yahoo';
    if (uaString.includes('Gunosy')) return 'inapp_android_gunosy';

    if (browser.name === 'Instagram') return 'inapp_android_instagram';
    if (browser.name === 'Line') return 'inapp_android_line';
    if (browser.name === 'Facebook') return 'inapp_android_facebook';
  }

  return null;
};

/**
 * カメラが使用可能かどうか
 */
export function isCameraUsable(): boolean {
  if (!window.navigator) return false;
  if (!window.navigator.mediaDevices) return false;
  if (!window.navigator.mediaDevices.getUserMedia) return false;
  if (!window.navigator.mediaDevices.getSupportedConstraints) return false;
  const constraints: MediaTrackSupportedConstraints = window.navigator.mediaDevices.getSupportedConstraints();
  if (!constraints.width) return false;
  if (!constraints.facingMode) return false;
  return true;
}
